import {
    BarChart3,
    DollarSign,
    PieChart,
    Plus,
    TrendingDown,
    TrendingUp
} from 'lucide-react';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const IncomeExpense = () => {
  const [activeTab, setActiveTab] = useState('profit-loss');
  const [periodType, setPeriodType] = useState('monthly'); // monthly, quarterly, yearly
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  
  const queryClient = useQueryClient();

  // 건물 목록 조회
  const { data: buildingsData, isLoading: isLoadingBuildings } = useQuery(
    ['buildings'],
    async () => {
      const response = await api.get('/income-expense/buildings');
      return response;
    },
    {
      enabled: activeTab === 'building-profit'
    }
  );

  // 기간별 손익 현황 조회
  const { data: profitLossData, isLoading: isLoadingProfitLoss } = useQuery(
    ['profit-loss', periodType, selectedPeriod],
    async () => {
      const response = await api.get(`/income-expense/profit-loss`, {
        params: { period_type: periodType, period: selectedPeriod }
      });
      return response;
    },
    {
      enabled: activeTab === 'profit-loss'
    }
  );

  // 건물별 손익 현황 조회
  const { data: buildingProfitLossData, isLoading: isLoadingBuildingProfitLoss } = useQuery(
    ['building-profit-loss', selectedBuilding, periodType, selectedPeriod],
    async () => {
      if (!selectedBuilding) return null;
      const response = await api.get(`/income-expense/building-profit-loss/${selectedBuilding.id}`, {
        params: { period_type: periodType, period: selectedPeriod }
      });
      return response;
    },
    {
      enabled: activeTab === 'building-profit' && selectedBuilding !== null
    }
  );

  // 추가수입 목록 조회
  const { data: additionalIncomeData, isLoading: isLoadingIncome, refetch: refetchIncomeQuery } = useQuery(
    ['additional-income', selectedPeriod],
    async () => {
      const response = await api.get(`/income-expense/additional-income`, {
        params: { period: selectedPeriod }
      });
      return response;
    },
    {
      enabled: activeTab === 'additional-income'
    }
  );

  // 비용 목록 조회
  const { data: expenseData, isLoading: isLoadingExpense, refetch: refetchExpenseQuery } = useQuery(
    ['expenses', selectedPeriod],
    async () => {
      const response = await api.get(`/income-expense/expenses`, {
        params: { period: selectedPeriod }
      });
      return response;
    },
    {
      enabled: activeTab === 'expenses'
    }
  );

  // 수입 데이터 재조회 함수
  const refetchIncome = (period) => {
    setSelectedPeriod(period);
    refetchIncomeQuery();
  };

  // 비용 데이터 재조회 함수
  const refetchExpense = (period) => {
    setSelectedPeriod(period);
    refetchExpenseQuery();
  };

  // 추가수입 등록
  const addIncomeMutation = useMutation(
    async (incomeData) => {
      const response = await api.post('/income-expense/additional-income', incomeData);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['additional-income']);
        queryClient.invalidateQueries(['profit-loss']);
        setShowIncomeModal(false);
      }
    }
  );

  // 비용 등록
  const addExpenseMutation = useMutation(
    async (expenseData) => {
      const response = await api.post('/income-expense/expenses', expenseData);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses']);
        queryClient.invalidateQueries(['profit-loss']);
        setShowExpenseModal(false);
      }
    }
  );

  const tabs = [
    { id: 'profit-loss', name: '기간별손익현황', icon: BarChart3 },
    { id: 'building-profit', name: '건물별수익관리', icon: PieChart },
    { id: 'additional-income', name: '추가수입등록', icon: TrendingUp },
    { id: 'expenses', name: '비용등록', icon: TrendingDown }
  ];

  const periodOptions = [
    { value: 'monthly', label: '월별' },
    { value: 'quarterly', label: '분기별' },
    { value: 'yearly', label: '년도별' }
  ];

  const generatePeriodOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    if (periodType === 'monthly') {
      for (let i = 11; i >= 0; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const value = date.toISOString().slice(0, 7);
        const label = `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
        options.push({ value, label });
      }
    } else if (periodType === 'quarterly') {
      for (let i = 3; i >= 0; i--) {
        const year = currentDate.getFullYear();
        const quarter = Math.floor(currentDate.getMonth() / 3) + 1 - i;
        if (quarter <= 0) {
          const prevYear = year - 1;
          const prevQuarter = quarter + 4;
          options.push({ 
            value: `${prevYear}-Q${prevQuarter}`, 
            label: `${prevYear}년 ${prevQuarter}분기` 
          });
        } else {
          options.push({ 
            value: `${year}-Q${quarter}`, 
            label: `${year}년 ${quarter}분기` 
          });
        }
      }
    } else if (periodType === 'yearly') {
      for (let i = 4; i >= 0; i--) {
        const year = currentDate.getFullYear() - i;
        options.push({ 
          value: year.toString(), 
          label: `${year}년` 
        });
      }
    }
    
    return options;
  };

  const renderProfitLossTab = () => {
    if (isLoadingProfitLoss) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    const data = profitLossData || {};
  const totalIncome = (data.rentalIncome || 0) + (data.managementFeeIncome || 0) + (data.parkingFeeIncome || 0) + (data.repairFeeIncome || 0) + (data.otherIncome || 0);
  const totalExpense = data.totalExpense || 0;
    const netProfit = totalIncome - totalExpense;

    return (
      <div className="space-y-6">
        {/* 기간 선택 */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">기간 유형:</label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {periodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">기간:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {generatePeriodOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 손익 요약 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 수입</p>
                <p className="text-2xl font-bold text-green-600">
                  ₩{totalIncome.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 비용</p>
                <p className="text-2xl font-bold text-red-600">
                  ₩{totalExpense.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${netProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <TrendingUp className={`w-6 h-6 ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">순이익</p>
                <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₩{netProfit.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PieChart className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">수익률</p>
                <p className="text-2xl font-bold text-blue-600">
                  {totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 상세 내역 테이블 */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  항목
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  금액
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  부가세
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  합계
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* 수입 항목들 */}
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  임대료 수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.rentalIncome || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩0
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ₩{(data.rentalIncome || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  관리비수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.managementFeeIncomeAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.managementFeeIncomeVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ₩{(data.managementFeeIncome || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  주차비수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.parkingFeeIncomeAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.parkingFeeIncomeVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ₩{(data.parkingFeeIncome || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  수리비수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.repairFeeIncomeAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.repairFeeIncomeVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ₩{(data.repairFeeIncome || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  기타수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.otherIncomeAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.otherIncomeVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ₩{(data.otherIncome || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-100 border-t-2 border-green-300">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  총 수입
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ₩{((data.rentalIncome || 0) + (data.managementFeeIncomeAmount || 0) + (data.parkingFeeIncomeAmount || 0) + (data.repairFeeIncomeAmount || 0) + (data.otherIncomeAmount || 0)).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ₩{((data.managementFeeIncomeVat || 0) + (data.parkingFeeIncomeVat || 0) + (data.repairFeeIncomeVat || 0) + (data.otherIncomeVat || 0)).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  ₩{totalIncome.toLocaleString()}
                </td>
              </tr>
              
              {/* 비용 항목들 */}
              <tr className="bg-red-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  관리비
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.managementFeeAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.managementFeeVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                  ₩{(data.managementFee || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-red-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  유지보수비
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.maintenanceCostAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.maintenanceCostVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                  ₩{(data.maintenanceCost || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-red-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  기타비용
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.otherExpensesAmount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₩{(data.otherExpensesVat || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                  ₩{(data.otherExpenses || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-red-100 border-t-2 border-red-300">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  총 비용
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ₩{((data.managementFeeAmount || 0) + (data.maintenanceCostAmount || 0) + (data.otherExpensesAmount || 0)).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ₩{((data.managementFeeVat || 0) + (data.maintenanceCostVat || 0) + (data.otherExpensesVat || 0)).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                  ₩{totalExpense.toLocaleString()}
                </td>
              </tr>
              
              {/* 순이익 */}
              <tr className="bg-blue-100 border-t-4 border-blue-300">
                <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-gray-900">
                  순이익
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-gray-900">
                  ₩{(totalIncome - totalExpense).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-gray-900">
                  -
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-blue-600">
                  ₩{(totalIncome - totalExpense).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAdditionalIncomeTab = () => {
    if (isLoadingIncome) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    const incomeList = additionalIncomeData || [];

    return (
      <div className="space-y-6">
        {/* 기간 선택 */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">기간 선택</h3>
          <div className="flex gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                년도
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                월
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const month = i + 1;
                  return (
                    <option key={month} value={month.toString().padStart(2, '0')}>
                      {month}월
                    </option>
                  );
                })}
              </select>
            </div>
            <button
              onClick={() => {
                const period = `${selectedYear}-${selectedMonth}`;
                refetchIncome(period);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg mt-6"
            >
              조회
            </button>
          </div>
        </div>

        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">추가 수입 목록</h3>
          <button
            onClick={() => setShowIncomeModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            추가수입 등록
          </button>
        </div>

        {/* 추가수입 목록 테이블 */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  등록일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수입 항목
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  적요
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  금액
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  부가세
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  합계
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {incomeList.map((income) => (
                <tr key={income.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(income.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {income.category}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {income.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    ₩{income.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    ₩{income.vat.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-700">
                    ₩{income.total.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderExpensesTab = () => {
    if (isLoadingExpense) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    const expenseList = expenseData || [];

    return (
      <div className="space-y-6">
        {/* 기간 선택 */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">기간 선택</h3>
          <div className="flex gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                년도
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                월
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const month = i + 1;
                  return (
                    <option key={month} value={month.toString().padStart(2, '0')}>
                      {month}월
                    </option>
                  );
                })}
              </select>
            </div>
            <button
              onClick={() => {
                const period = `${selectedYear}-${selectedMonth}`;
                refetchExpense(period);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg mt-6"
            >
              조회
            </button>
          </div>
        </div>

        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">비용 목록</h3>
          <button
            onClick={() => setShowExpenseModal(true)}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            비용 등록
          </button>
        </div>

        {/* 비용 목록 테이블 */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  등록일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  비용 항목
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  적요
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  금액
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  부가세
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  합계
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenseList.map((expense) => (
                <tr key={expense.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(expense.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {expense.category}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                    ₩{expense.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                    ₩{expense.vat.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-700">
                    ₩{expense.total.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // 건물별 수익관리 탭 렌더링
  const renderBuildingProfitTab = () => {
    if (isLoadingBuildings) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* 건물 선택 및 기간 설정 */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 건물 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물 선택
              </label>
              <select
                value={selectedBuilding?.id || ''}
                onChange={(e) => {
                  const buildingId = e.target.value;
                  const building = buildingsData?.find(b => b.id === parseInt(buildingId));
                  setSelectedBuilding(building || null);
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">건물을 선택하세요</option>
                {buildingsData?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name} ({building.total_units}세대, {building.active_contracts}계약)
                  </option>
                ))}
              </select>
            </div>

            {/* 기간 타입 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기간 타입
              </label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 기간 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기간
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {generatePeriodOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 건물별 손익 현황 */}
        {selectedBuilding && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedBuilding.name} 손익 현황
            </h3>
            
            {isLoadingBuildingProfitLoss ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : buildingProfitLossData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 총 수입 */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">총 수입</p>
                      <p className="text-2xl font-bold text-green-900">
                        ₩{buildingProfitLossData.total_income?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 총 비용 */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingDown className="h-8 w-8 text-red-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-600">총 비용</p>
                      <p className="text-2xl font-bold text-red-900">
                        ₩{buildingProfitLossData.total_expenses?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 순이익 */}
                <div className={`p-4 rounded-lg ${buildingProfitLossData.net_profit >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                  <div className="flex items-center">
                    <DollarSign className={`h-8 w-8 ${buildingProfitLossData.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${buildingProfitLossData.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        순이익
                      </p>
                      <p className={`text-2xl font-bold ${buildingProfitLossData.net_profit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                        ₩{buildingProfitLossData.net_profit?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 수익률 */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <PieChart className="h-8 w-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">수익률</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {buildingProfitLossData.profit_margin?.toFixed(1) || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                건물을 선택하고 기간을 설정해주세요.
              </div>
            )}
          </div>
        )}

        {/* 건물 목록 */}
        {!selectedBuilding && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">보유 건물 목록</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {buildingsData?.map((building) => (
                <div
                  key={building.id}
                  onClick={() => setSelectedBuilding(building)}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all"
                >
                  <h4 className="font-medium text-gray-900">{building.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{building.address}</p>
                  <div className="flex justify-between mt-2 text-sm text-gray-500">
                    <span>총 {building.total_units}세대</span>
                    <span>활성 {building.active_contracts}계약</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">수입비용관리</h1>
          <p className="text-gray-600">수입과 비용을 체계적으로 관리하고 손익을 분석하세요</p>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 컨텐츠 */}
      <div className="mt-6">
        {activeTab === 'profit-loss' && renderProfitLossTab()}
        {activeTab === 'building-profit' && renderBuildingProfitTab()}
        {activeTab === 'additional-income' && renderAdditionalIncomeTab()}
        {activeTab === 'expenses' && renderExpensesTab()}
      </div>

      {/* 추가수입 등록 모달 */}
      {showIncomeModal && (
        <AdditionalIncomeModal
          onClose={() => setShowIncomeModal(false)}
          onSubmit={addIncomeMutation.mutate}
          isLoading={addIncomeMutation.isLoading}
        />
      )}

      {/* 비용 등록 모달 */}
      {showExpenseModal && (
        <ExpenseModal
          onClose={() => setShowExpenseModal(false)}
          onSubmit={addExpenseMutation.mutate}
          isLoading={addExpenseMutation.isLoading}
        />
      )}
    </div>
  );
};

// 추가수입 등록 모달 컴포넌트
const AdditionalIncomeModal = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    building_id: '',
    category: '',
    amount: '',
    vat: '',
    description: '',
    date: new Date().toISOString().slice(0, 10)
  });

  // 건물 목록 조회
  const { data: buildingsData } = useQuery(
    ['buildings-for-modal'],
    async () => {
      const response = await api.get('/income-expense/buildings');
      return response;
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
      vat: parseFloat(formData.vat)
    });
  };

  const incomeCategories = [
    '임대보증금',
    '관리비수입',
    '주차비수입',
    '수리비수입',
    '기타수입',
    '보상금',
    '수수료'
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">추가수입 등록</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                건물 선택
              </label>
              <select
                value={formData.building_id}
                onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">전체 건물</option>
                {buildingsData?.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                수입 항목
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">선택하세요</option>
                {incomeCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                금액
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="금액을 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                부가세
              </label>
              <input
                type="number"
                value={formData.vat}
                onChange={(e) => setFormData({ ...formData, vat: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="부가세를 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                등록일
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                적요
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                rows={3}
                placeholder="수입에 대한 적요를 입력하세요"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 비용 등록 모달 컴포넌트
const ExpenseModal = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    building_id: '',
    category: '',
    amount: '',
    vat: '',
    description: '',
    date: new Date().toISOString().slice(0, 10)
  });

  // 건물 목록 조회
  const { data: buildingsData } = useQuery(
    ['buildings-for-modal'],
    async () => {
      const response = await api.get('/income-expense/buildings');
      return response;
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
      vat: parseFloat(formData.vat)
    });
  };

  const expenseCategories = [
    '관리비',
    '유지보수비',
    '공과금',
    '보험료',
    '세금',
    '수수료',
    '청소비',
    '경비비',
    '기타비용'
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">비용 등록</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                건물 선택
              </label>
              <select
                value={formData.building_id}
                onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">전체 건물</option>
                {buildingsData?.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비용 항목
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">선택하세요</option>
                {expenseCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                금액
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="금액을 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                부가세
              </label>
              <input
                type="number"
                value={formData.vat}
                onChange={(e) => setFormData({ ...formData, vat: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="부가세를 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                등록일
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                적요
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                rows={3}
                placeholder="비용에 대한 적요를 입력하세요"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IncomeExpense;